import React, { useState, useEffect } from "react";
import VoucherInstancesFilters from "./VoucherInstancesFilters";
import VoucherAnalyticsChart from "./VoucherAnalyticsChart";
import {
  getVoucherInstances,
  getVoucherDailyAnalytics,
  type VoucherInstance,
  type VoucherInstanceFilters,
  type VoucherDailyAnalytics,
} from "../../services/voucherInstances.service";
import { formatDate, formatCurrency } from "../../utils/formatters";
import Card from "../ui/Card";
import LoadingSpinner from "../common/LoadingSpinner";

const VoucherInstancesTab: React.FC = () => {
  const [instances, setInstances] = useState<VoucherInstance[]>([]);
  const [total, setTotal] = useState(0);
  const [analytics, setAnalytics] = useState<VoucherDailyAnalytics[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<VoucherInstanceFilters>({});
  const limit = 20;

  useEffect(() => {
    loadInstances();
  }, [page, filters]);

  useEffect(() => {
    loadAnalytics();
  }, [filters]);

  const loadInstances = async () => {
    setIsLoading(true);
    try {
      const response = await getVoucherInstances({ page, limit, ...filters });
      setInstances(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error("Error loading voucher instances:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const data = await getVoucherDailyAnalytics(filters);
      setAnalytics(data);
    } catch (error) {
      console.error("Error loading analytics:", error);
    }
  };

  const handleFilterChange = (newFilters: VoucherInstanceFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <VoucherInstancesFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* Analytics Chart */}
      {analytics.length > 0 && (
        <Card padding="lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Daily Analytics
          </h3>
          <VoucherAnalyticsChart data={analytics} />
        </Card>
      )}

      {/* Instances Table */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Voucher Instances ({total.toLocaleString()})
          </h3>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : instances.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No voucher instances found
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Template
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Redeemed
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {instances.map((instance) => (
                    <tr key={instance.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm font-medium text-gray-900">
                          {instance.voucher_code}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {instance.template_name || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            instance.status === "active"
                              ? "bg-green-100 text-green-800"
                              : instance.status === "redeemed"
                                ? "bg-blue-100 text-blue-800"
                                : instance.status === "expired"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {instance.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(instance.voucher_value)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(instance.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {instance.redeemed_at
                          ? formatDate(instance.redeemed_at)
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {(page - 1) * limit + 1} to{" "}
                  {Math.min(page * limit, total)} of {total}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * limit >= total}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default VoucherInstancesTab;
